import { api_configuration } from "../../common";
import api_model = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types;
import api_client_protocol_reply = api_model.client_protocol_reply.alan_api;
import api_client_protocol_request = api_model.client_protocol_request.alan_api;
import api_client_reply = api_model.client_reply.alan_api;
import api_client_request = api_model.client_request.alan_api;
import client_request_serializer = api_model.client_request.serializer_json;
import client_protocol_request_serializer = api_model.client_protocol_request.serializer_json;
import read_api_client_reply = api_model.client_reply.alan_api;

export class DataStoreProtocolError extends Error {}
export class InvalidTokenError extends DataStoreProtocolError {}

interface QueueState {
	start?: () => void;
	stop?: () => void;
	send: (item: () => void) => void;
}
class Buffered_Queue {
	private item_queue: (() => void)[] = [];
	private started_state: QueueState = {
		stop: () => {
			this.queue_state = this.stopped_state;
		},
		send: (item) => {
			item();
		},
	};
	private stopped_state: QueueState = {
		start: () => {
			this.item_queue.forEach(this.started_state.send);
			this.item_queue = [];
			this.queue_state = this.started_state;
		},
		send: (item) => {
			this.item_queue.push(item);
		},
	};
	private queue_state = this.stopped_state;
	send(item: () => void) {
		this.queue_state.send(item);
	}

	start() {
		this.queue_state.start?.();
	}

	stop() {
		this.queue_state.stop?.();
	}
}

type ResultHandlers<T> = {
	handleSuccess: (result: T) => void;
	handleFailure: (error_message: string) => void;
};
type SubscriptionInfo = {
	subscription: api_client_request.Cclient_request;
	subscribed_handler: (subscriptionResult: read_api_client_reply.Csubscription) => void;
	notification_handler: (notification: read_api_client_reply.Cnotification) => void;
	error_handler?: () => void;
	unsubscribe: () => void;
};

export class DatastoreConnection {
	on_announcement(handler: (announcement: string) => void): void {
		this.announcement_handler = handler;
	}
	token: string | undefined;
	notification_handler?: (notification: api_client_protocol_reply.Cnotification) => void;
	private ws: WebSocket;
	private ws_id: number;
	private queue: Buffered_Queue;
	private reconnect_delay = 100;
	private connected = true;
	private first_time_connection = true;
	private onHandlers = {
		"invalid token": [] as (() => void)[],
		disconnect: [] as (() => void)[],
		reconnect: [] as (() => void)[],
		connect: [] as (() => void)[],
	};
	private last_client_token = 0;
	private protocol_requests = new Map<number, (reply: api_client_protocol_reply.Creply) => void>();
	private announcement_handler: ((message: string) => void) | undefined;

	constructor(token: string | undefined) {
		this.token = token;
		this.queue = new Buffered_Queue();
		this.connectWebSocket();
	}

	disconnectWebSocket(): void {
		this.token = undefined;
		this.queue.stop();
		if (this.ws) {
			//the WebSocket could be uninitialized.
			this.ws.onclose = noop;
			this.ws.close();
		}
		this.connected = true;
	}

	async getServerTime(): Promise<number> {
		const result = await this.queueProtocolRequest("server time");
		switch (result.properties.result.state.name) {
			case "invalid subscription id":
				throw new Error("unexpected state");
			case "invalid token":
				throw new InvalidTokenError();
			case "server error": {
				throw new DataStoreProtocolError("server error");
			}
			case "success": {
				const $ = result.properties.result.state.node;
				return $.properties.type.cast("server time").properties.julian_time.value;
			}
			default:
				unexpectedState(result.properties.result.state);
		}
	}

	async getAuthenticationUri(): Promise<string> {
		const reply = await this.queueProtocolRequest("authentication uri");
		switch (reply.properties.result.state.name) {
			case "invalid subscription id":
				throw new Error("unexpected state");
			case "invalid token":
				throw new InvalidTokenError();
			case "server error": {
				throw new DataStoreProtocolError("server error");
			}
			case "success": {
				const $ = reply.properties.result.state.node;
				return $.properties.type.cast("authentication uri").properties.uri;
			}
			default:
				unexpectedState(reply.properties.result.state);
		}
	}

	async validateToken(token: string): Promise<boolean> {
		const reply = await this.queueProtocolRequest("server time", token);
		switch (reply.properties.result.state.name) {
			case "invalid subscription id":
				throw new Error("unexpected state");
			case "invalid token":
				return false;
			case "server error": {
				throw new DataStoreProtocolError("server error");
			}
			case "success":
				return true;
			default:
				unexpectedState(reply.properties.result.state);
		}
	}

	isWebSocketOpen(): boolean {
		const OPEN = 1;
		return this.ws !== undefined && this.ws.readyState === OPEN;
	}

	on(handler: keyof DatastoreConnection["onHandlers"], callback: () => void): this {
		this.onHandlers[handler].push(callback);
		return this;
	}

	async queueProtocolRequest(
		protocol_request: api_model.client_protocol_request.alan_api.Trequest["type"],
		alternative_token?: string
	): Promise<api_client_protocol_reply.Creply> {
		return new Promise((resolve) => {
			this.queue.send(async () => {
				const reply = await this.sendProtocolRequest(protocol_request, alternative_token);
				resolve(reply);
			});
		});
	}

	sendUnSubscribeMessage(key: string): void {
		this.queue.send(() => {
			const message = api_client_protocol_request.Cclient_protocol_request.create({
				"access type": this.token
					? [
							"authenticated",
							{
								token: this.token,
							},
					  ]
					: ["anonymous", {}],
				type: [
					"unsubscribe",
					{
						"subscription id": key,
					},
				],
			});
			this.ws.send(JSON.stringify(client_protocol_request_serializer.serialize(message)));
		});
	}

	private connectWebSocket() {
		if (this.isWebSocketOpenOrConnecting()) {
			return;
		}

		if (
			typeof window !== "undefined" &&
			(typeof window["WebSocket"] === "function" || typeof window["WebSocket"] === "object")
		) {
			const protocol = window.location.protocol === "https:" ? "wss" : "ws";
			const target = protocol + "://" + window.location.host + window.location.pathname + "websocket";
			this.ws = new WebSocket(target);
			this.ws.onopen = () => {
				this.ws_id = Date.now();
				this.queue.start();
				if (this.first_time_connection) {
					this.first_time_connection = false;
					this.notify("connect");
				}
				this.reconnect_delay = 100;
				if (!this.connected) {
					this.connected = true;
					this.notify("reconnect");
				}
			};
			this.ws.onmessage = (msg) => {
				this.handleMessage(api_client_protocol_reply.Cclient_protocol_reply.create(JSON.parse(msg.data)));
			};
			this.ws.onclose = (e) => {
				this.queue.stop();
				if (e.code !== 1000) {
					this.reconnect();
				}
			};
		}
	}

	private async sendProtocolRequest(
		protocol_request: api_model.client_protocol_request.alan_api.Trequest["type"],
		alternative_token?: string
	): Promise<api_client_protocol_reply.Creply> {
		const new_client_token = this.nextClientToken();
		const request_ws_id = this.ws_id;
		const token = alternative_token ?? this.token;
		const serialized_request = JSON.stringify(
			client_protocol_request_serializer.serialize(
				api_client_protocol_request.Cclient_protocol_request.create({
					"access type": token
						? [
								"authenticated",
								{
									token: token,
								},
						  ]
						: ["anonymous", {}],
					type: [
						"request",
						{
							"client token": new_client_token,
							type: protocol_request,
						},
					],
				})
			)
		);
		this.ws.send(serialized_request);
		return new Promise((resolve) => {
			this.protocol_requests.set(new_client_token, (protocol_reply) => {
				if (this.ws_id !== request_ws_id) {
					return;
				}
				resolve(protocol_reply);
			});
		});
	}

	private reconnect() {
		this.reconnect_delay = this.reconnect_delay > 3000 ? 3000 : this.reconnect_delay + 100;
		if (this.connected) {
			this.connected = false;
			this.notify("disconnect");
		}
		setTimeout(() => {
			this.connectWebSocket();
		}, this.reconnect_delay);
	}

	private handleMessage(message: api_client_protocol_reply.Cclient_protocol_reply) {
		switch (message.properties.type.state.name) {
			case "notification": {
				const notification_result = message.properties.type.state.node;
				this.notification_handler?.(notification_result);
				break;
			}
			case "reply": {
				const reply = message.properties.type.state.node;
				this.protocol_requests.get(reply.properties.client_token.value)?.(reply);
				this.protocol_requests.delete(reply.properties.client_token.value);

				switch (reply.properties.result.state.name) {
					case "invalid subscription id":
						break; //handled in the request handler.
					case "invalid token": {
						this.token = undefined;
						this.notify("invalid token");
						break;
					}
					case "server error":
						break; //handled in the request handler.
					case "success":
						break; //handled in the request handler.
					default:
						unexpectedState(reply.properties.result.state);
				}
				break;
			}
			case "unsubscribe": {
				//FIXME: maybe resubscribe?
				break;
			}
			case "announcement": {
				const announcement = message.properties.type.state.node;
				this.announcement_handler?.(announcement.properties.announcement);
				break;
			}
			default:
				unexpectedState(message.properties.type.state);
		}
	}

	private notify(handlers: keyof DatastoreConnection["onHandlers"]) {
		this.onHandlers[handlers].forEach((handler) => {
			handler();
		});
	}

	private nextClientToken() {
		if (this.last_client_token === Number.MAX_SAFE_INTEGER) {
			return (this.last_client_token = 0);
		}
		return ++this.last_client_token;
	}

	private isWebSocketOpenOrConnecting() {
		const CONNECTING = 0;
		const OPEN = 1;
		return this.ws !== undefined && (this.ws.readyState === CONNECTING || this.ws.readyState === OPEN);
	}
}

export class DatastoreProxy {
	static instance: DatastoreProxy;
	application_model: api_model.application.alan_api.Capplication;
	readonly connection: DatastoreConnection;
	private subscriptions = new Map<string, SubscriptionInfo>();
	private last_subscription_id = Date.now();
	subscription_counter = 0;
	query_counter = 0;
	subscribeOnce_counter = 0;

	constructor(application_model: api_model.application.alan_api.Capplication, connection: DatastoreConnection) {
		this.application_model = application_model;
		this.connection = connection;

		this.connection.notification_handler = (notification_result) => {
			const sub_id = notification_result.properties.subscription_id;
			const subscription_context = this.subscriptions.get(sub_id);
			if (subscription_context !== undefined) {
				const notification: read_api_client_reply.Cclient_reply = api_client_reply.Cclient_reply.create(
					JSON.parse(notification_result.properties.notification),
					{
						application: this.application_model,
						request: subscription_context["subscription"],
					}
				);
				subscription_context.notification_handler(notification.properties.type.cast("notification"));
				notification.properties.type.cast("notification").properties.context.switch({
					exists: undefined,
					"does not exist": () => {
						this.subscriptions.delete(sub_id);
					},
					"permission denied": () => {
						this.subscriptions.delete(sub_id);
					},
				});
			}
		};
		this.connection.on("reconnect", () => {
			this.reSubscribeAll();
		});
	}

	async execute(command: api_client_request.Tcommand_execution): Promise<api_client_reply.Ccommand_execution> {
		const decorated_request = this.decorateClientRequest({
			type: ["command execution", command],
		});
		const reply = await this.connection.queueProtocolRequest([
			"mutation",
			{
				request: JSON.stringify(client_request_serializer.serialize(decorated_request)),
			},
		]);
		return this.handleApplicationReply(reply, decorated_request).properties.type.cast("command execution");
	}

	async changePassword(old_password: string, new_password: string): Promise<api_client_reply.Cpassword_mutation> {
		const decorated_request = this.decorateClientRequest({
			type: [
				"password mutation",
				{
					"new password": new_password,
					"old password": old_password,
				},
			],
		});
		const reply = await this.connection.queueProtocolRequest([
			"mutation",
			{
				request: JSON.stringify(client_request_serializer.serialize(decorated_request)),
			},
		]);
		return this.handleApplicationReply(reply, decorated_request).properties.type.cast("password mutation");
	}

	async resetPassword(username: string): Promise<api_client_reply.Cpassword_reset> {
		const decorated_request = this.decorateClientRequest({
			type: [
				"password reset",
				{
					username: username,
				},
			],
		});
		const reply = await this.connection.queueProtocolRequest([
			"mutation",
			{
				request: JSON.stringify(client_request_serializer.serialize(decorated_request)),
			},
		]);
		return this.handleApplicationReply(reply, decorated_request).properties.type.cast("password reset");
	}

	async collectionQuery(
		collection_query: api_model.client_request.alan_api.Tcollection_query,
		result_handlers?: ResultHandlers<api_model.client_reply.alan_api.Ccollection_query>,
		query_type: "client query" | "report query" = "client query"
	): Promise<api_client_reply.Ccollection_query> {
		this.query_counter++;
		const decorated_request = this.decorateClientRequest({
			type: ["collection query", collection_query],
		});
		const reply = await this.connection.queueProtocolRequest([
			query_type,
			{
				request: JSON.stringify(client_request_serializer.serialize(decorated_request)),
			},
		]);

		const client_reply = this.handleApplicationReply(
			reply,
			decorated_request,
			result_handlers?.handleFailure
		).properties.type.cast("collection query");
		if (result_handlers) {
			result_handlers.handleSuccess(client_reply);
		}
		return client_reply;
	}

	async reportQuery(
		collection_query: api_model.client_request.alan_api.Tcollection_query,
		result_handlers: ResultHandlers<api_client_reply.Ccollection_query>
	): Promise<api_client_reply.Ccollection_query> {
		return this.collectionQuery(collection_query, result_handlers, "report query");
	}

	async update(
		mutation: api_model.client_request.alan_api.Tmutation
	): Promise<api_model.client_reply.alan_api.Cmutation> {
		const decorated_request = this.decorateClientRequest({
			type: ["mutation", mutation],
		});
		const reply = await this.connection.queueProtocolRequest([
			"mutation",
			{
				request: JSON.stringify(client_request_serializer.serialize(decorated_request)),
			},
		]);
		return this.handleApplicationReply(reply, decorated_request).properties.type.cast("mutation");
	}

	subscribeOnce(
		subscription: api_model.client_request.alan_api.Tsubscription,
		onSubscribed: (subscriptionResult: read_api_client_reply.Csubscription) => void,
		onError?: () => void
	): void;
	subscribeOnce(
		subscription: api_model.client_request.alan_api.Tsubscription
	): Promise<read_api_client_reply.Csubscription>;
	subscribeOnce(
		subscription: api_model.client_request.alan_api.Tsubscription,
		onSubscribed?: (subscriptionResult: read_api_client_reply.Csubscription) => void,
		onError?: () => void
	): void | Promise<read_api_client_reply.Csubscription> {
		this.subscription_counter--;
		this.subscribeOnce_counter++;
		if (onSubscribed !== undefined) {
			const datastore_subscription = this.subscribe(
				subscription,
				(result) => {
					datastore_subscription.unsubscribe();
					onSubscribed(result);
				},
				noop,
				onError
			);
		} else {
			return new Promise((resolve, reject) => {
				const datastore_subscription = this.subscribe(
					subscription,
					(result) => {
						datastore_subscription.unsubscribe();
						resolve(result);
					},
					noop,
					() => {
						reject();
					}
				);
			});
		}
	}

	subscribe(
		subscription: api_model.client_request.alan_api.Tsubscription,
		onSubscribed: (subscriptionResult: read_api_client_reply.Csubscription) => void,
		onNotification: (notification: read_api_client_reply.Cnotification) => void,
		onError?: () => void
	): { unsubscribe: () => void } {
		this.subscription_counter++;
		const key = this.generateSubscriptionId();
		const decorated_subscription = api_client_request.Cclient_request.create(
			{ type: ["subscription", subscription] },
			{
				application: this.application_model,
			}
		);
		const subscription_info: SubscriptionInfo = {
			unsubscribe: () => {
				this.subscriptions.delete(key);
				subscription_info.unsubscribe = noop;
				subscription_info.subscribed_handler = () => {
					this.connection.sendUnSubscribeMessage(key);
				};
			},
			subscription: decorated_subscription,
			subscribed_handler: (result) => {
				subscription_info.unsubscribe = () => {
					this.subscriptions.delete(key);
					subscription_info.unsubscribe = noop;
					this.connection.sendUnSubscribeMessage(key);
				};
				onSubscribed(result);
			},
			notification_handler: onNotification,
			error_handler: onError,
		};
		this.subscriptions.set(key, subscription_info);
		this.sendSubscriptionMessage(key, subscription_info);
		return {
			unsubscribe: () => {
				subscription_info.unsubscribe();
			},
		};
	}

	reSubscribeAll(): void {
		for (const subscription_info of this.subscriptions) {
			this.sendSubscriptionMessage(subscription_info[0], subscription_info[1]);
		}
	}

	unSubscribeAll(): void {
		for (const subscription_info of this.subscriptions) {
			subscription_info[1].unsubscribe();
		}
	}

	on(handler: keyof DatastoreConnection["onHandlers"], callback: () => void): this {
		this.connection.on(handler, callback);
		if (handler === "connect" && this.connection.isWebSocketOpen()) {
			callback();
		}
		return this;
	}

	on_announcement(handler: (announcement: string) => void): void {
		this.connection.on_announcement(handler);
	}

	private handleApplicationReply(
		reply: api_client_protocol_reply.Creply,
		request: api_client_request.Cclient_request,
		on_error?: (message: string) => void
	): api_client_reply.Cclient_reply {
		switch (reply.properties.result.state.name) {
			case "invalid subscription id":
				throw new Error("unexpected state");
			case "invalid token":
				throw new InvalidTokenError();
			case "server error": {
				on_error?.("server error");
				throw new DataStoreProtocolError("server error");
			}
			case "success": {
				const $ = reply.properties.result.state.node;
				const client_reply = api_client_reply.Cclient_reply.create(
					JSON.parse($.properties.type.cast("application").properties.result),
					{
						application: this.application_model,
						request: request,
					}
				);
				return client_reply;
			}
			default:
				unexpectedState(reply.properties.result.state);
		}
	}

	private async sendSubscriptionMessage(sub_id: string, subscription_info: SubscriptionInfo) {
		const reply = await this.connection.queueProtocolRequest([
			"subscribe",
			{
				"subscription id": sub_id,
				subscription: JSON.stringify(client_request_serializer.serialize(subscription_info.subscription)),
			},
		]);
		if (!this.subscriptions.has(sub_id)) {
			return;
		}
		switch (reply.properties.result.state.name) {
			case "invalid subscription id": {
				this.subscriptions.delete(sub_id);
				subscription_info.error_handler?.();
				break;
			}
			case "invalid token":
				break;
			case "server error": {
				this.subscriptions.delete(sub_id);
				subscription_info.error_handler?.();
				break;
			}
			case "success": {
				const $ = reply.properties.result.state.node.properties.type.cast("subscribe");
				const subscription_result: read_api_client_reply.Cclient_reply = api_client_reply.Cclient_reply.create(
					JSON.parse($.properties.initial_state),
					{
						application: this.application_model,
						request: subscription_info.subscription,
					}
				);
				subscription_info.subscribed_handler(subscription_result.properties.type.cast("subscription"));
				subscription_result.properties.type.cast("subscription").properties.context.switch({
					exists: undefined,
					"does not exist": () => {
						this.subscriptions.delete(sub_id);
					},
					"permission denied": () => {
						this.subscriptions.delete(sub_id);
					},
				});
				break;
			}
			default:
				unexpectedState(reply.properties.result.state);
		}
	}

	private generateSubscriptionId() {
		return "" + this.last_subscription_id++;
	}

	private decorateClientRequest(request: api_model.client_request.alan_api.Tclient_request) {
		return api_client_request.Cclient_request.create(request, { application: this.application_model });
	}
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop(): void {}

// For debugging. In DatastoreProxy.subscribe insert the line:
// console.log(log_subscription(subscription));
/*
import { Raw_Id_Path_Head } from "../navigation/IdPathBuilder";
function log_subscription(subscription: api_model.client_request.alan_api.Tsubscription): string {
	return (
		(function step(path: Raw_Id_Path_Head): string {
			switch (path["has steps"][0]) {
				case "no":
					return "";
				case "yes": {
					const type = path["has steps"][1].type;
					const tail = path["has steps"][1].tail;
					switch (type[0]) {
						case "group":
							return ".'" + type[1]["group"] + "'" + step(tail);
						case "state":
							return ".'" + type[1]["state group"] + "'?'" + type[1]["state"] + "'" + step(tail);
						case "collection entry":
							return ".'" + type[1]["collection"] + "'['" + type[1]["id"] + "']" + step(tail);
						default:
							throw new Error("unexpected step");
					}
				}
				default:
					throw new Error("unexpected step");
			}
		})(subscription["context node"] as any as Raw_Id_Path_Head) +
		" " +
		(function step(properties: api_model.client_request.alan_api.Tsubscribed_properties, base_indent: string): string {
			let result = "{\n";
			for (const key in properties["properties"]) {
				const indent = base_indent + "  ";
				const property = properties["properties"][key];
				let prop_res = ".'" + key + "'";
				switch (property["type"][0]) {
					case "collection": {
						const collection = <api_model.client_request.alan_api.Tcollection__type__properties__subscribed_properties>(
							property["type"][1]
						);
						prop_res = prop_res + "[] ";
						switch (collection["subscribe on"][0]) {
							case "entries":
								prop_res =
									prop_res +
									step(
										(<api_model.client_request.alan_api.Tentries__subscribe_on>collection["subscribe on"][1])[
											"subscribed properties"
										],
										indent + "  "
									);
								break;
							case "updates":
								prop_res = prop_res + "<updates>";
								break;
						}
						break;
					}
					case "group": {
						const group = <api_model.client_request.alan_api.Tgroup__type__properties__subscribed_properties>(
							property["type"][1]
						);
						prop_res = prop_res + " " + step(group["subscribed properties"], indent + "  ");
						break;
					}
					case "state group": {
						const stategroup = <
							api_model.client_request.alan_api.Tstate_group__type__properties__subscribed_properties
						>property["type"][1];
						prop_res = prop_res + " (\n";
						for (const key in stategroup["states"]) {
							const state = stategroup["states"][key];
							prop_res =
								prop_res + indent + "  " + key + " " + step(state["subscribed properties"], indent + "    ") + "\n";
						}
						prop_res = prop_res + indent + ")";
						break;
					}
					case "number":
						break;
					case "text":
						break;
					case "file":
						break;
					default:
						throw new Error("unexpected step");
				}
				result = result + indent + prop_res + "\n";
			}
			result = result + base_indent + "}";
			return result;
		})(subscription["subscribed properties"], "")
	);
}
*/
